import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import {
    fetchNotifications,
    fetchUnreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    setFilters,
    clearFilters,
    resetNotifications,
    mergeNotifications,
    selectNotifications,
    selectUnreadCount,
    selectNotificationsLoading,
    selectNotificationsError,
    selectHasMore,
    selectCurrentOffset,
    selectFilters,
} from '@/store/slices/notificationsSlice';
import type { NotificationCategory, NotificationType } from '@/types/notifications';
import { notificationsApi } from '@/api/notificationsApi';

/**
 * Основной хук для работы с уведомлениями
 */
export const useNotifications = () => {
    const dispatch = useDispatch<AppDispatch>();

    // Селекторы
    const notifications = useSelector((state: RootState) => selectNotifications(state));
    const unreadCount = useSelector((state: RootState) => selectUnreadCount(state));
    const loading = useSelector((state: RootState) => selectNotificationsLoading(state));
    const error = useSelector((state: RootState) => selectNotificationsError(state));
    const hasMore = useSelector((state: RootState) => selectHasMore(state));
    const currentOffset = useSelector((state: RootState) => selectCurrentOffset(state));
    const filters = useSelector((state: RootState) => selectFilters(state));

    // Загрузить уведомления
    const loadNotifications = useCallback(
        (refresh: boolean = false) => {
            const offset = refresh ? 0 : currentOffset;
            if (refresh) {
                dispatch(resetNotifications());
            }
            dispatch(fetchNotifications({ offset, ...filters }));
        },
        [dispatch, currentOffset, filters]
    );

    // Загрузить следующую страницу
    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            dispatch(fetchNotifications({ offset: currentOffset, ...filters }));
        }
    }, [dispatch, loading, hasMore, currentOffset, filters]);

    // Загрузить счетчик непрочитанных
    const loadUnreadCount = useCallback(() => {
        dispatch(fetchUnreadCount());
    }, [dispatch]);

    // Отметить как прочитанное
    const markAsRead = useCallback(
        (notificationId: number) => {
            dispatch(markNotificationAsRead(notificationId));
        },
        [dispatch]
    );

    // Отметить все как прочитанные
    const markAllAsRead = useCallback(() => {
        dispatch(markAllNotificationsAsRead());
    }, [dispatch]);

    // Удалить уведомление
    const deleteOne = useCallback(
        (notificationId: number) => {
            dispatch(deleteNotification(notificationId));
        },
        [dispatch]
    );

    // Применить фильтры
    const applyFilters = useCallback(
        (newFilters: {
            isRead?: boolean;
            category?: NotificationCategory;
            type?: NotificationType;
        }) => {
            dispatch(setFilters(newFilters));
        },
        [dispatch]
    );

    // Очистить фильтры
    const resetFilters = useCallback(() => {
        dispatch(clearFilters());
    }, [dispatch]);

    // Обновить (refresh)
    const refresh = useCallback(() => {
        loadNotifications(true);
        loadUnreadCount();
    }, [loadNotifications, loadUnreadCount]);

    // Синхронизация в фоне (без сброса локальных данных)
    const syncInBackground = useCallback(
        async (filterParams?: {
            isRead?: boolean;
            category?: NotificationCategory;
            type?: NotificationType;
        }) => {
            try {
                const response = await notificationsApi.getNotifications({
                    limit: 20,
                    offset: 0,
                    ...filterParams,
                });
                dispatch(mergeNotifications(response.data || []));
                dispatch(fetchUnreadCount());
            } catch (error) {
                console.error('Background sync failed:', error);
            }
        },
        [dispatch]
    );

    return {
        // Данные
        notifications,
        unreadCount,
        loading,
        error,
        hasMore,
        filters,

        // Методы
        loadNotifications,
        loadMore,
        loadUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteOne,
        applyFilters,
        resetFilters,
        refresh,
        syncInBackground,
    };
};

/**
 * Хук для автоматической загрузки уведомлений при монтировании
 */
export const useNotificationsAutoLoad = (loadOnMount: boolean = true) => {
    const notifications = useNotifications();

    useEffect(() => {
        if (loadOnMount) {
            notifications.loadNotifications(true);
            notifications.loadUnreadCount();
        }
    }, [loadOnMount]);

    return notifications;
};

/**
 * Хук только для счетчика непрочитанных (легковесный)
 */
export const useUnreadCount = (autoLoad: boolean = true) => {
    const dispatch = useDispatch<AppDispatch>();
    const unreadCount = useSelector((state: RootState) => selectUnreadCount(state));

    const load = useCallback(() => {
        dispatch(fetchUnreadCount());
    }, [dispatch]);

    useEffect(() => {
        if (autoLoad) {
            load();
        }
    }, [autoLoad]);

    return { unreadCount, reload: load };
};

/**
 * Хук для периодической проверки новых уведомлений
 */
export const useNotificationsPolling = (intervalMs: number = 60000) => {
    const { loadUnreadCount } = useNotifications();

    useEffect(() => {
        const interval = setInterval(() => {
            loadUnreadCount();
        }, intervalMs);

        return () => clearInterval(interval);
    }, [loadUnreadCount, intervalMs]);
};
